import { BaseApiService } from './BaseApiService';
import type { ChartData } from '../types/market';

export interface TechnicalIndicators {
  symbol: string;
  price: number;
  sma20: number;
  sma50: number;
  sma200: number;
  rsi: number;
  macd: number;
  macdSignal: number;
  volume: number;
  avgVolume: number;
  support: number;
  resistance: number;
  trend: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  momentum: 'STRONG' | 'WEAK' | 'NEUTRAL';
}

export class TechnicalAnalysisService {
  /**
   * Calculate technical indicators from chart data
   */
  static calculateTechnicalIndicators(chartData: ChartData): TechnicalIndicators {
    const data = chartData.data;
    if (data.length === 0) {
      throw new Error('No chart data available for technical analysis');
    }

    const prices = data.map(d => d.close);
    const volumes = data.map(d => d.volume);
    const currentPrice = prices[prices.length - 1];
    const currentVolume = volumes[volumes.length - 1];

    // Calculate Simple Moving Averages
    const sma20 = this.calculateSMA(prices, 20);
    const sma50 = this.calculateSMA(prices, 50);
    const sma200 = this.calculateSMA(prices, 200);

    // Calculate RSI
    const rsi = this.calculateRSI(prices, 14);

    // Calculate MACD
    const { macd, signal } = this.calculateMACD(prices);

    // Calculate support and resistance
    const { support, resistance } = this.calculateSupportResistance(data);

    // Calculate average volume
    const avgVolume = volumes.reduce((sum, vol) => sum + vol, 0) / volumes.length;

    // Determine trend
    const trend = this.determineTrend(currentPrice, sma20, sma50, sma200);

    // Determine momentum
    const momentum = this.determineMomentum(rsi, macd, signal);

    return {
      symbol: chartData.symbol,
      price: currentPrice,
      sma20,
      sma50,
      sma200,
      rsi,
      macd,
      macdSignal: signal,
      volume: currentVolume,
      avgVolume,
      support,
      resistance,
      trend,
      momentum,
    };
  }

  /**
   * Calculate Simple Moving Average
   */
  private static calculateSMA(prices: number[], period: number): number {
    if (prices.length < period) return prices[prices.length - 1] || 0;
    
    const recentPrices = prices.slice(-period);
    return recentPrices.reduce((sum, price) => sum + price, 0) / period;
  }

  /**
   * Calculate RSI (Relative Strength Index)
   */
  private static calculateRSI(prices: number[], period: number = 14): number {
    if (prices.length < period + 1) return 50; // Neutral RSI

    const changes = [];
    for (let i = 1; i < prices.length; i++) {
      changes.push(prices[i] - prices[i - 1]);
    }

    const recentChanges = changes.slice(-period);
    const gains = recentChanges.filter(change => change > 0);
    const losses = recentChanges.filter(change => change < 0).map(loss => Math.abs(loss));

    const avgGain = gains.length > 0 ? gains.reduce((sum, gain) => sum + gain, 0) / period : 0;
    const avgLoss = losses.length > 0 ? losses.reduce((sum, loss) => sum + loss, 0) / period : 0;

    if (avgLoss === 0) return 100;
    
    const rs = avgGain / avgLoss;
    return 100 - (100 / (1 + rs));
  }

  /**
   * Calculate MACD (Moving Average Convergence Divergence)
   */
  private static calculateMACD(prices: number[]): { macd: number; signal: number } {
    if (prices.length < 26) return { macd: 0, signal: 0 };

    const ema12 = this.calculateEMA(prices, 12);
    const ema26 = this.calculateEMA(prices, 26);
    const macd = ema12 - ema26;

    // For simplicity, using SMA instead of EMA for signal line
    const macdValues = [macd]; // In real implementation, you'd have historical MACD values
    const signal = macd; // Simplified signal line

    return { macd, signal };
  }

  /**
   * Calculate Exponential Moving Average
   */
  private static calculateEMA(prices: number[], period: number): number {
    if (prices.length < period) return prices[prices.length - 1] || 0;

    const multiplier = 2 / (period + 1);
    let ema = prices[0];

    for (let i = 1; i < prices.length; i++) {
      ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
    }

    return ema;
  }

  /**
   * Calculate support and resistance levels
   */
  private static calculateSupportResistance(data: any[]): { support: number; resistance: number } {
    const highs = data.map(d => d.high);
    const lows = data.map(d => d.low);

    // Simple approach: use recent highs and lows
    const recentData = data.slice(-20); // Last 20 periods
    const recentHighs = recentData.map(d => d.high);
    const recentLows = recentData.map(d => d.low);

    const resistance = Math.max(...recentHighs);
    const support = Math.min(...recentLows);

    return { support, resistance };
  }

  /**
   * Determine trend based on moving averages
   */
  private static determineTrend(price: number, sma20: number, sma50: number, sma200: number): 'BULLISH' | 'BEARISH' | 'NEUTRAL' {
    if (price > sma20 && sma20 > sma50 && sma50 > sma200) {
      return 'BULLISH';
    } else if (price < sma20 && sma20 < sma50 && sma50 < sma200) {
      return 'BEARISH';
    } else {
      return 'NEUTRAL';
    }
  }

  /**
   * Determine momentum based on RSI and MACD
   */
  private static determineMomentum(rsi: number, macd: number, signal: number): 'STRONG' | 'WEAK' | 'NEUTRAL' {
    const rsiStrong = rsi > 70 || rsi < 30;
    const macdStrong = Math.abs(macd - signal) > 0.5;

    if (rsiStrong && macdStrong) {
      return 'STRONG';
    } else if (rsi > 40 && rsi < 60 && Math.abs(macd - signal) < 0.2) {
      return 'WEAK';
    } else {
      return 'NEUTRAL';
    }
  }
}